import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox as InboxIcon } from "lucide-react";
import { api } from "@/lib/api";

const POLL_MS       = 45_000;
const SEEN_KEY      = "md_admin_inbox_seen_ids";
const RECENT_LIMIT  = 5;

function timeAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1)  return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

function readSeen() {
    try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
    catch { return new Set(); }
}
function persistSeen(idSet) {
    try { localStorage.setItem(SEEN_KEY, JSON.stringify([...idSet])); }
    catch {}
}

/**
 * Admin-only bell that polls /api/admin/inbox for unread contact submissions.
 * Renders nothing for non-admins. Highlights + shows a red badge with the
 * unseen count. Click opens a dropdown with the 5 most recent — each row
 * deep-links to /admin/inbox?open={id}.
 */
export default function AdminInboxBell() {
    const [items, setItems]     = useState([]);     // last N received
    const [total, setTotal]     = useState(0);      // total received count from server
    const [open, setOpen]       = useState(false);
    const [unseen, setUnseen]   = useState(0);      // items whose id is not in seen set
    const seenRef = useRef(readSeen());
    const wrapRef = useRef(null);

    const refresh = async () => {
        try {
            const { data } = await api.get(`/admin/inbox?status=received&limit=${RECENT_LIMIT}`);
            const rows = data?.submissions || [];
            setItems(rows);
            setTotal(data?.counts?.received ?? rows.length);
            const seen = seenRef.current;
            const unseenCount = rows.reduce((n, r) => (seen.has(r.id) ? n : n + 1), 0);
            setUnseen(unseenCount);
        } catch (err) {
            // Non-admin session or transient error — silently ignore for UI,
            // but surface in console so infra can spot repeat failures.
            if (process.env.NODE_ENV !== "production" && typeof console !== "undefined") console.warn("[AdminInboxBell] refresh failed:", err?.message || err);
        }
    };

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, POLL_MS);
        // Refresh when window regains focus (admin returning from another tab)
        const onFocus = () => refresh();
        window.addEventListener("focus", onFocus);
        return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
    }, []);

    // Close dropdown on outside click / escape
    useEffect(() => {
        if (!open) return;
        const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        const onKey   = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onClick);
        window.addEventListener("keydown", onKey);
        return () => { document.removeEventListener("mousedown", onClick); window.removeEventListener("keydown", onKey); };
    }, [open]);

    const markVisibleAsSeen = () => {
        const next = new Set(seenRef.current);
        items.forEach((r) => next.add(r.id));
        seenRef.current = next;
        persistSeen(next);
        setUnseen(0);
    };

    const toggleOpen = () => {
        if (!open) markVisibleAsSeen();  // opening = you've seen them
        setOpen((o) => !o);
    };

    return (
        <div ref={wrapRef} className="relative" data-testid="admin-inbox-bell-wrap">
            <button
                onClick={toggleOpen}
                aria-label={`Admin inbox — ${unseen} new`}
                className="relative p-2 rounded-md hover:bg-secondary transition-colors"
                data-testid="admin-inbox-bell"
            >
                <InboxIcon className={`w-4 h-4 ${unseen > 0 ? "text-primary" : "text-muted-foreground"}`} />
                {unseen > 0 && (
                    <>
                        <span
                            className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] font-extrabold flex items-center justify-center animate-pulse"
                            data-testid="admin-inbox-bell-badge"
                        >
                            {unseen > 9 ? "9+" : unseen}
                        </span>
                        <span className="sr-only">{unseen} new submissions</span>
                    </>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 top-full mt-2 w-[340px] bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-50"
                    role="menu"
                    data-testid="admin-inbox-bell-dropdown"
                >
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                        <div>
                            <div className="overline text-primary">Admin inbox</div>
                            <div className="text-sm font-extrabold tracking-tight">
                                {total} unread submission{total === 1 ? "" : "s"}
                            </div>
                        </div>
                        <Link to="/admin/inbox" onClick={() => setOpen(false)}
                              className="text-xs font-semibold text-primary hover:underline"
                              data-testid="admin-inbox-bell-view-all">
                            Open inbox →
                        </Link>
                    </div>

                    {items.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-muted-foreground"
                             data-testid="admin-inbox-bell-empty">
                            No unread submissions.
                        </div>
                    ) : (
                        <ul className="max-h-[360px] overflow-y-auto divide-y divide-border/50"
                            data-testid="admin-inbox-bell-list">
                            {items.map((s) => (
                                <li key={s.id}>
                                    <Link to={`/admin/inbox?open=${s.id}`} onClick={() => setOpen(false)}
                                          className="block px-4 py-3 hover:bg-muted/50 transition-colors"
                                          data-testid={`admin-inbox-bell-item-${s.id}`}>
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                            <span className="font-bold text-sm truncate">{s.name || "—"}</span>
                                            <span className="text-[10px] text-muted-foreground whitespace-nowrap">{timeAgo(s.ts)}</span>
                                        </div>
                                        <p className="text-xs text-foreground/80 line-clamp-2 leading-snug">
                                            {s.message}
                                        </p>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
